import { useNavigate } from 'react-router-dom';
import { Fragment, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import NavigationBar from '../common/NavigationBar';
import { useLazyQuery } from '@apollo/client';
import { GET_CURRENT_USER, GET_GROUPS, GET_ORGANIZATION } from '../../graphQL/queries';
import { IsLoggedIn, getAppId, setAppId, generateAppId } from '../../redux/tokenSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setCurrentOrganization, setCurrentUser, setUserGroup } from '../../redux/appStateSlice';
import { Paths } from '../routes/Routes';
import LoadingSpinner from './LoadingSpinner';
import { initializeApp } from "firebase/app";
import { getMessaging, onMessage, getToken, Messaging } from "firebase/messaging";
import { RegisterPushToken } from "../../graphQL/api";
import { DmwaError } from "../../models/DmwaError";
import { Stack } from 'react-bootstrap';
import { isIos, renderBannerInstall, renderIOSBannerInstall } from '../../helpers/renderHelper';
import ReactGA from "react-ga4";

const NavBarWrapper = (props) => {

	// IMPORTANT: network-only fetch policy is required to prevent showing stale user data
	// in shared device scenarios. While Apollo cache is cleared on login (Login.tsx:82),
	// this provides additional protection against cached data from previously logged-in users.
	// Without this, the app could briefly display another user's data before the cache updates.
	const [getCurrentUser, { loading, data }] = useLazyQuery(GET_CURRENT_USER, {
		fetchPolicy: 'network-only'
	});

	const [getGroups, getGroupResponses] = useLazyQuery(GET_GROUPS);

	const [hasLoadedAll, setHasLoadedAll] = useState(false)

	// Your web app's Firebase configuration
	// For Firebase JS SDK v7.20.0 and later, measurementId is optional
	const firebaseConfig = useMemo(() => ({
		apiKey: import.meta.env.REACT_APP_FIREBASE_API_KEY,
		authDomain: import.meta.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
		projectId: import.meta.env.REACT_APP_FIREBASE_PROJECT_ID,
		storageBucket: import.meta.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
		messagingSenderId: import.meta.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
		appId: import.meta.env.REACT_APP_FIREBASE_APP_ID,
		measurementId: import.meta.env.REACT_APP_FIREBASE_MEASUREMENT_ID
	}), []);

	// Initialize Firebase
	const app = initializeApp(firebaseConfig);

	var messaging: Messaging | undefined

	try {
		// isSupported().then(supported => {
		// 	if (supported) {
		// 		messaging = getMessaging(app);
		// 	}
		// })
		messaging = getMessaging(app);
	} catch (e) {
		//console.log('Messaging error ', e)
	}

	const [getCurrentOrganization, organizationResponses] = useLazyQuery(GET_ORGANIZATION);

	const [notificationToken, setNotificationToken] = useState('');

	//const [showLoading, setShowLoading] = useState(false)
	const showLoading = loading || organizationResponses.loading || getGroupResponses.loading


	const [loadingTitle, setLoadingTitle] = useState('')
	const isLoggedIn = IsLoggedIn();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const currentUser = useAppSelector(state => state.appState.currentUser);
	const bondedPairToken = useAppSelector(state => state.token.bondedPairToken);

	const registerTokenWithServer = useCallback(() => {
		if (notificationToken) {

			var appDeviceId = getAppId();

			//console.log("Initial value of app device id is :", appDeviceId);


			if (!appDeviceId) {
				//console.log("Generating new app device id: ");

				appDeviceId = generateAppId();
				//console.log("Generated app device id is ", appDeviceId);

				dispatch(setAppId(appDeviceId as string))
			}

			//console.log("Calling register push with app device id", appDeviceId);

			RegisterPushToken(notificationToken, appDeviceId, response => {
				if (response instanceof DmwaError) {
					if (response.status === 403) {
						response.message = "Could not register token";
					}
					// else {
					// 	dispatch(setHasRegisteredPush(true))
					// }
				}
			})
		}

		setHasLoadedAll(true)
	}, [notificationToken, dispatch])

	const requestToken = useCallback(async () => {
		// Get registration token. Initially this makes a network call, once retrieved
		// subsequent calls to getToken will return from cache.

		if (messaging) {
			const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

			getToken(messaging, { serviceWorkerRegistration: registration, vapidKey: import.meta.env.REACT_APP_FIREBASE_CLOUD_MESSAGING_PUBLIC_KEY }).then((currentToken) => {
				if (currentToken) {
					setNotificationToken(currentToken);
				} else {
					// Show permission request UI
					//console.log('No registration token available. Request permission to generate one.');
					// ...
				}
			}).catch((err) => {
				//console.log('An error occurred while retrieving token. ', err);
				// ...
			});
		}
	}, [messaging, firebaseConfig])

	const requestPermission = useCallback(() => {

		//console.log('Requesting permission...');
		try {
			Notification.requestPermission().then((permission) => {
				if (permission === 'granted') {
					//console.log('Notification permission granted.');
					requestToken();
				}
			});
		} catch (error) {
			//console.error("Error caught in the outer try-catch block:", error);
			setHasLoadedAll(true)
		}
	}, [requestToken])

	// This variable will save the event for later use.
	const deferredPrompt = useRef<any>();
	window.addEventListener('beforeinstallprompt', (e) => {
		// Prevents the default mini-infobar or install dialog from appearing on mobile
		e.preventDefault();
		// Save the event because you'll need to trigger it later.
		deferredPrompt.current = e;
		//console.log("deferredPrompt = ", deferredPrompt)
		// Show your customized install prompt for your PWA
	});

	async function processDefferedPrompt() {
		// const { outcome } = await deferredPrompt?.current?.userChoice;
		// console.log('outcome = ', outcome)

		// // deferredPrompt?.current?.userChoice.then(outcome => {
		// // 	console.log('outcome = ', outcome)
		// // })
	}

	useEffect(()=> {
		if (hasLoadedAll) {
			processDefferedPrompt()
		}
	},[hasLoadedAll, deferredPrompt])

	useEffect(() => {
		if (isLoggedIn && currentUser) {
			if (!notificationToken) {
				requestPermission();
			} else {
				registerTokenWithServer();
			}
		}
	}, [notificationToken, isLoggedIn, currentUser, registerTokenWithServer, requestPermission])

	useEffect(() => {
		if (organizationResponses.data?.organization) {
			dispatch(setCurrentOrganization(organizationResponses.data?.organization))

			if (organizationResponses.data.organization.organizationID && import.meta.env.REACT_APP_GA_ID) {
				const organizationID = organizationResponses.data.organization.organizationID;
				const organizationName = organizationResponses.data.organization.name;
				
				if (organizationID === 60) {
					window[`ga-disable-${import.meta.env.REACT_APP_GA_ID}`] = true;
					//console.log('GA completely disabled for organization ID:60 Salesdemo');
				} else {
					window[`ga-disable-${import.meta.env.REACT_APP_GA_ID}`] = false;
					
					ReactGA.gtag('set', 'user_properties', {
						var1: organizationName ? String(organizationName) : null
					});
					
					ReactGA.gtag('config', import.meta.env.REACT_APP_GA_ID, {
						var3: import.meta.env.REACT_APP_GA_ENVIRONMENT || 'Gridline',
						var1: organizationName ? String(organizationName) : null
					});
				}
			}

			//Fetch groups after we have current user
			if (!data?.currentUser?.isInDriverRole) {
				//setShowLoading(true)
				setLoadingTitle("groups")
				getGroups({
					variables: {
						isLocationGroup: true,
						hasDrivers: true,
						startDate: import.meta.env.REACT_APP_MANAGER_START_DATE ? import.meta.env.REACT_APP_MANAGER_START_DATE : null,
						endDate: import.meta.env.REACT_APP_MANAGER_END_DATE ? import.meta.env.REACT_APP_MANAGER_END_DATE : null,
						prevStartDate: import.meta.env.REACT_APP_MANAGER_PREV_START_DATE ? import.meta.env.REACT_APP_MANAGER_PREV_START_DATE : null,
						prevEndDate: import.meta.env.REACT_APP_MANAGER_PREV_END_DATE ? import.meta.env.REACT_APP_MANAGER_PREV_END_DATE : null
					}
				});
			}
		}
	}, [organizationResponses.data?.organization, data?.currentUser?.isInDriverRole, dispatch, getGroups])

	useEffect(() => {
		if (getGroupResponses?.data?.groups) {
			dispatch(setUserGroup({ userId: currentUser?.userID ?? -1, groups: getGroupResponses?.data?.groups }));
		}
	}, [getGroupResponses?.data, currentUser?.userID, dispatch])

	useEffect(() => {
		if (!isLoggedIn) {
			navigate(Paths.LOGIN)
		} else {
			if (!currentUser) {
				setLoadingTitle("user")

				getCurrentUser({
					variables: {
						startDate: null,
						endDate: null,
						prevStartDate: null,
						prevEndDate: null
					}
				})
			}
		}
	}, [isLoggedIn, currentUser, getCurrentUser, navigate, bondedPairToken])

	useEffect(() => {
		if (data?.currentUser) {
			//setShowLoading(false)
			dispatch(setCurrentUser(data?.currentUser))

			if (data.currentUser.userID && import.meta.env.REACT_APP_GA_ID) {
				ReactGA.gtag('set', 'user_properties', {
					var2: String(data.currentUser.userID)
				});

				ReactGA.gtag('config', import.meta.env.REACT_APP_GA_ID, {
					var2: String(data.currentUser.userID)
				});
			}

			if (isLoggedIn) {
				setLoadingTitle("organization")
				getCurrentOrganization()
			}

		}
	}, [data?.currentUser, dispatch, getCurrentOrganization, isLoggedIn])

	useEffect(() => {
		if (messaging) {
			onMessage(messaging, (payload) => {
				//console.log('Message received. ', payload);

				const title = (payload?.notification as any).title;
				const body = (payload?.notification as any).body;
				const icon = (payload?.notification as any).icon;

				// Create and display a notification here
				const notification = new Notification(title ?? "", {
					body,
					icon: icon ?? `${import.meta.env.BASE_URL}/logo192.png`,
				});

				notification.onclick = () => {
					// Handle notification click event
					// You can redirect the user to a specific page or take any other action here
					navigate(Paths.HOME)
				};
			});
		}

	}, [messaging, navigate])

	

	return (
		<Fragment>
			<NavigationBar title={props.title} isHome={props.isHome} />
			{showLoading ?
				<LoadingSpinner title={loadingTitle} />
				:
				<Stack style={{marginTop:55}}>
					{ hasLoadedAll && !isIos() && deferredPrompt.current && renderBannerInstall(deferredPrompt)}
					{ renderIOSBannerInstall() }
					{props.children}
				</Stack>
			}
		</Fragment>
	)
}

export default NavBarWrapper;